import { apiGet, apiPost, apiDelete } from './client';
import type { OperationResult, PagedItemsResult } from './types';

// ai.AiModelPricing — LLM pricing rates per model × provider, time-bounded.
// All rates are USD per 1,000,000 tokens (matches OpenAI's pricing page format).

export interface AiModelPricing {
  id: number;
  modelName: string;
  displayName?: string;
  provider: string;
  inputPer1MTokens: number;
  outputPer1MTokens: number;
  cachedInputPer1MTokens?: number | null;
  reasoningOutputPer1MTokens?: number | null;
  effectiveDate: string;            // ISO
  expirationDate?: string | null;   // ISO; null = currently active
  isActive: boolean;
  notes?: string;
  createdDate?: string;
  lastUpdatedDate?: string;
}

export interface ModelPricingSearchParams {
  search?: string;
  pageNo?: number;
  pageSize?: number;
}

export async function searchModelPricing(
  params: ModelPricingSearchParams = {}
): Promise<PagedItemsResult<AiModelPricing>> {
  const { search = '', pageNo = 1, pageSize = 200 } = params;
  const wrapped = await apiGet<OperationResult<PagedItemsResult<AiModelPricing>>>(
    '/api/AiModelPricing/Search',
    { search, pageNo, pageSize }
  );
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || 'Failed to load model pricing');
  }
  return wrapped.data;
}

export async function getModelPricingById(id: number): Promise<AiModelPricing> {
  const wrapped = await apiGet<OperationResult<AiModelPricing>>(`/api/AiModelPricing/GetById/${id}`);
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || `Pricing row ${id} not found`);
  }
  return wrapped.data;
}

export async function addModelPricing(model: Partial<AiModelPricing>): Promise<AiModelPricing> {
  const wrapped = await apiPost<OperationResult<AiModelPricing>>('/api/AiModelPricing/Add', model);
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || 'Failed to add pricing');
  }
  return wrapped.data;
}

export async function updateModelPricing(model: Partial<AiModelPricing>): Promise<AiModelPricing> {
  const wrapped = await apiPost<OperationResult<AiModelPricing>>('/api/AiModelPricing/Update', model);
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || 'Failed to update pricing');
  }
  return wrapped.data;
}

export async function removeModelPricing(id: number): Promise<void> {
  const wrapped = await apiDelete<OperationResult>(`/api/AiModelPricing/Remove/${id}`);
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to delete pricing');
}
